/**
 * QueryCombobox — a generic async combobox that owns its display and selection state.
 *
 * The caller provides a query result (e.g. from TanStack Query) and a search
 * callback. The component handles debouncing, input display, item merging,
 * and controlled/uncontrolled selection.
 *
 * The items and selection management follows Base UI's official async combobox
 * example (https://base-ui.com/react/components/combobox#async-search-single,
 * source: https://github.com/mui/base-ui — docs/src/app/(docs)/react/components/combobox/demos/async-single).
 * Key patterns borrowed from that example:
 * - `filter={null}` — server handles filtering, not the combobox
 * - `onOpenChangeComplete` — caches [selected] on close so Base UI can
 *   resolve the label without the full results list being mounted
 * - Selected item merged into items only when not actively searching,
 *   so stale selections don't linger in search results
 */
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/frontend/components/ui/combobox'

/** Minimal return shape expected from the caller's query hook. */
export interface QueryResult<TData> {
  data: TData | undefined
  isFetching: boolean
}

interface QueryComboboxProps<TItem, TData> {
  /** Query result — e.g. the return value of a TanStack Query hook. */
  query: QueryResult<TData>
  /** Extract the items array from the query data. */
  getItems: (data: TData | undefined) => TItem[]
  /**
   * Called with the debounced search string when the user types. Wire this
   * to the state that drives the caller's query hook.
   */
  onSearchChange: (search: string) => void
  /** Controlled selection — the caller owns the selected item. */
  value?: TItem | null
  /** Uncontrolled — seeds the selection on mount only. Ignored when `value` is provided. */
  initialValue?: TItem | null
  /** Called when the user selects or clears a value. */
  onValueChange?: (item: TItem | null) => void
  /** Equality check for matching items (e.g. by ID). */
  isEqual: (a: TItem, b: TItem) => boolean
  /** Stable unique key for React reconciliation (e.g. item ID). */
  getKey: (item: TItem) => string
  /** Extract the display label from an item. */
  getLabel: (item: TItem) => string
  /** Render function for each item in the dropdown list. */
  renderItem: (item: TItem) => React.ReactNode
  /** Minimum characters before search fires (default: 2). */
  minSearchLength?: number
  /** Debounce delay in ms (default: 300). */
  debounceMs?: number
  /** Message shown when no results match the search (default: "No results found."). */
  emptyMessage?: string
  /** Message shown before the user has typed enough to trigger a search (default: "Type to search…"). */
  searchPrompt?: string

  // Pass-through props for the combobox input
  id?: string
  placeholder?: string
  disabled?: boolean
  onBlur?: () => void
  showClear?: boolean
  className?: string
  'aria-invalid'?: boolean
}

export function QueryCombobox<TItem, TData>({
  query,
  getItems,
  onSearchChange,
  value: controlledValue,
  initialValue,
  onValueChange,
  isEqual,
  getKey,
  getLabel,
  renderItem,
  minSearchLength = 2,
  debounceMs = 300,
  emptyMessage = 'No results found.',
  searchPrompt = 'Type to search\u2026',
  id,
  placeholder,
  disabled,
  onBlur,
  showClear = true,
  className,
  'aria-invalid': ariaInvalid,
}: QueryComboboxProps<TItem, TData>) {
  const isControlled = controlledValue !== undefined

  // --- Selection state ---
  const [internalSelection, setInternalSelection] = useState<TItem | null>(initialValue ?? null)
  const selected = isControlled ? (controlledValue ?? null) : internalSelection

  // --- Display value ---
  const [displayValue, setDisplayValue] = useState(selected ? getLabel(selected) : '')

  // Sync display value when controlled value changes externally.
  // No deps array — a deps array would compare object references, but we need
  // semantic equality via `isEqual`. The ref tracks the previous value instead.
  const prevControlledRef = useRef(controlledValue)
  useEffect(() => {
    if (!isControlled) return
    const prev = prevControlledRef.current
    prevControlledRef.current = controlledValue

    if (prev === undefined) return

    const changed =
      controlledValue === null ? prev !== null : prev === null || !isEqual(controlledValue, prev)

    if (changed) {
      setDisplayValue(controlledValue ? getLabel(controlledValue) : '')
    }
  })

  // --- Debounced search ---
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Tracks whether the user is actively searching — set on keystroke, cleared
  // on selection. Drives which items list the combobox sees.
  const [isSearching, setIsSearching] = useState(false)
  // Track popup state so we can ignore input value changes fired during close.
  const isOpenRef = useRef(false)
  // Immediate visual feedback — true as soon as the user types, before the
  // debounce fires. Cleared when the query settles.
  const [isPending, setIsPending] = useState(false)

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    []
  )

  const debouncedSearchChange = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (value.length >= minSearchLength) {
        setIsPending(true)
        debounceRef.current = setTimeout(() => {
          onSearchChange(value)
        }, debounceMs)
      } else {
        setIsPending(false)
        onSearchChange('')
      }
    },
    [onSearchChange, debounceMs, minSearchLength]
  )

  // Clear pending state once the query settles.
  useEffect(() => {
    if (!query.isFetching) setIsPending(false)
  }, [query.isFetching])

  // --- Items management ---
  const fetchedItems = getItems(query.data)

  // When the popup closes with a selection, cache it so Base UI can resolve
  // the label. While searching, use fetched results only — the selected item
  // shouldn't linger if it doesn't match the query.
  const [closedItems, setClosedItems] = useState<TItem[]>(selected ? [selected] : [])

  // Prefer fresh match from current results, fall back to our reference.
  const resolvedSelected = selected
    ? (fetchedItems.find((item) => isEqual(item, selected)) ?? selected)
    : null

  // Show fetched results only when the user is actively searching. Otherwise
  // show the closed-state cache (just the selected item for label resolution).
  const items = isSearching ? fetchedItems : closedItems

  const handleOpenChangeComplete = useCallback(
    (open: boolean) => {
      isOpenRef.current = open
      if (!open) {
        setIsSearching(false)
        setIsPending(false)
        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
          debounceRef.current = null
        }
        onSearchChange('')
        // Restore display to the selected item's label (user may have typed
        // without selecting, so displayValue could be stale search text).
        setDisplayValue(resolvedSelected ? getLabel(resolvedSelected) : '')
        if (resolvedSelected) {
          setClosedItems([resolvedSelected])
        }
      }
    },
    [resolvedSelected, getLabel, onSearchChange]
  )

  // --- Event handlers ---

  const handleValueChange = useCallback(
    (item: TItem | null) => {
      if (!isControlled) setInternalSelection(item)
      onValueChange?.(item)
      setDisplayValue(item ? getLabel(item) : '')
      setIsSearching(false)
      setIsPending(false)
      // Flush: clear debounce and reset search immediately on selection.
      if (debounceRef.current) clearTimeout(debounceRef.current)
      onSearchChange('')
    },
    [isControlled, onValueChange, getLabel, onSearchChange]
  )

  const handleInputValueChange = useCallback(
    (newValue: string, { reason }: { reason: string }) => {
      if (reason === 'item-press') return
      setDisplayValue(newValue)
      // Only treat as a search if the popup is open — Base UI may fire
      // input value changes during close (e.g. restoring the label).
      if (isOpenRef.current) {
        setIsSearching(true)
        debouncedSearchChange(newValue)
      }
    },
    [debouncedSearchChange]
  )

  return (
    <Combobox
      value={resolvedSelected}
      onValueChange={handleValueChange}
      onOpenChangeComplete={handleOpenChangeComplete}
      inputValue={displayValue}
      onInputValueChange={handleInputValueChange}
      filter={null}
      isItemEqualToValue={isEqual}
      itemToStringLabel={getLabel}
      items={items}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        loading={isSearching && (isPending || query.isFetching)}
        showClear={showClear}
        className={className}
      />
      <ComboboxContent>
        <ComboboxList>
          <ComboboxEmpty>{isSearching ? emptyMessage : searchPrompt}</ComboboxEmpty>
          <ComboboxCollection>
            {(item) => (
              <ComboboxItem key={getKey(item)} value={item}>
                {renderItem(item)}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
